"use client"

import type React from "react"

interface ButtonProps {
  type?: "button" | "submit" | "reset"
  onClick?: () => void
  children: React.ReactNode
  disabled?: boolean
  className?: string
  variant?: "primary" | "secondary" | "danger"
  loading?: boolean
  size?: "sm" | "md" | "lg"
}

const Button: React.FC<ButtonProps> = ({
  type = "button",
  onClick,
  children,
  disabled = false,
  className = "",
  variant = "primary",
  loading = false,
  size = "md",
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 border backdrop-blur-sm transform hover:scale-105"

  const sizeClasses = {
    sm: "px-2.5 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
    lg: "px-4 py-2 text-base",
  }

  const variantClasses = {
    primary:
      "bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white border-transparent focus:ring-blue-500 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300",
    secondary:
      "bg-white/80 hover:bg-white/90 text-gray-700 border-white/50 hover:border-white/70 focus:ring-blue-500 shadow-md hover:shadow-xl backdrop-blur-sm transition-all duration-300",
    danger: "bg-gradient-to-r from-red-500 via-pink-500 to-red-600 hover:from-red-600 hover:via-pink-600 hover:to-red-700 text-white border-transparent focus:ring-red-500 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300",
  }

  const disabledClasses = "opacity-50 cursor-not-allowed transform-none shadow-none"

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${
        disabled || loading ? disabledClasses : ""
      } ${className}`}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {children}
    </button>
  )
}

export default Button
